// src/components/dashboard/AgendaUpcoming.jsx
// Shows 5 upcoming calendar events on the homepage.

import { useState, useEffect } from 'react';
import { colors, fonts, cab } from '../../theme';
import { sbGet } from '../../lib/dashboardHelpers';

export default function AgendaUpcoming({ onNavigate, lawyerId }) {
  const [events, setEvents] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    (async () => {
      if (!lawyerId) { setEvents([]); return; }
      try {
        const from = new Date().toISOString();
        const to = new Date(Date.now() + 60 * 86400000).toISOString();
        const rows = await sbGet(
          `/calendar_events?lawyer_id=eq.${lawyerId}` +
          `&start_at=gte.${from}&start_at=lte.${to}` +
          `&order=start_at.asc&limit=5&select=id,title,start_at,end_at,location`
        );
        const parsed = (rows || []).map(ev => ({
          id: ev.id,
          summary: ev.title || '',
          location: ev.location || '',
          start: ev.start_at,
          end: ev.end_at,
          date: new Date(ev.start_at),
        }));
        setEvents(parsed);
      } catch {
        setEvents([]);
      }
    })();
  }, [lawyerId]);

  if (events === null) return null;

  if (events.length === 0) {
    return (
      <div style={{ padding: '28px 20px 30px', textAlign: 'center', fontFamily: fonts.sans }}>
        <div aria-hidden="true" style={{
          width: 46, height: 46, margin: '0 auto 14px', borderRadius: 14,
          background: 'var(--cab-civil-soft)', color: 'var(--cab-civil)',
          display: 'grid', placeItems: 'center',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.7" />
            <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </div>
        <div style={{ fontSize: 13.5, color: cab.ink2, fontWeight: 600 }}>Aucune échéance surveillée</div>
        <div style={{ fontSize: 12.5, color: cab.ink3, marginTop: 4, maxWidth: 270, margin: '4px auto 0', lineHeight: 1.5 }}>
          Connectez votre agenda pour que le cabinet veille sur vos audiences et délais.
        </div>
        <button
          onClick={() => onNavigate?.('settings')}
          style={{
            marginTop: 16, height: 36, padding: '0 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
            fontFamily: fonts.sans, fontWeight: 600, fontSize: 13, color: '#fff',
            background: `linear-gradient(180deg, ${cab.bordeauxHi}, ${cab.bordeaux})`,
            boxShadow: `0 8px 18px -8px ${cab.bordeauxGlow}`,
          }}
        >
          Connecter mon calendrier
        </button>
      </div>
    );
  }

  return (
    <div>
      {events.map((ev, i) => {
        const isToday = ev.date.toDateString() === new Date().toDateString();
        const summary = (ev.summary || '').toLowerCase();
        const isImportant = summary.includes('audience') || summary.includes('échéance')
          || summary.includes('expertise') || summary.includes('plaidoirie');

        return (
          <div
            key={i}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onNavigate?.('agenda')}
            style={{
              display: 'flex', gap: 12, alignItems: 'flex-start',
              padding: '6px 18px',
              borderBottom: i < events.length - 1 ? `1px solid ${colors.border}` : 'none',
              cursor: 'pointer',
              transition: 'background 0.15s',
              background: hovered === i ? colors.bgHover : 'transparent',
            }}
          >
            <div style={{
              fontSize: '0.81rem', fontWeight: 600,
              color: isToday ? colors.accent : colors.textPrimary,
              width: 44, flexShrink: 0,
              fontFamily: fonts.sans,
            }}>
              {ev.date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  fontSize: '0.81rem', fontWeight: 500, color: colors.textPrimary,
                  fontFamily: fonts.sans,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {ev.summary || 'Événement'}
                </span>
                {isImportant && (
                  <span style={{
                    background: colors.accentBg, color: colors.accent,
                    fontSize: '0.69rem', padding: '2px 8px', borderRadius: 4,
                    fontFamily: fonts.sans, fontWeight: 500, flexShrink: 0,
                  }}>
                    {isToday ? "Aujourd'hui" : 'Bientôt'}
                  </span>
                )}
              </div>
              <div style={{
                fontSize: '0.69rem', color: colors.textSecondary,
                fontFamily: fonts.sans, marginTop: 2,
              }}>
                {isToday ? "Aujourd'hui" : ev.date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                {ev.location ? ` · ${ev.location}` : ''}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
